import { useState, useEffect } from 'react';
import { Mail, MapPin, CheckCircle2, Truck, Pencil, Loader2 } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import revisionSheetService from '../../services/revisionSheetService';
import { CANADIAN_PROVINCES } from '../../data/canadianProvinces';

// Fiches de révision papier — formulaire d'adresse postale (une demande par niveau).
// Ouvert via la notification "Recevez vos fiches de révision" (relatedUrl
// /dashboard/account/revision-sheets) ou depuis le menu du compte.

const POSTAL_RE = /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/;

const Field = ({ label, value, onChange, placeholder = '', required = true, className = '' }) => (
    <div className={className}>
        <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wider">
            {label}{required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
        <input
            type="text" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
            className="w-full px-4 py-2.5 text-base md:text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
        />
    </div>
);

// ── Carte d'un niveau éligible ──
const LevelSheetCard = ({ level, prefill, language, onSubmitted }) => {
    const fr = language === 'fr';
    const request = level.request;
    const [editing, setEditing] = useState(!request); // pas de demande → formulaire direct
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [form, setForm] = useState(() => ({
        lang: request?.lang || (fr ? 'fr' : 'en'),
        firstName: request?.firstName || prefill.firstName || '',
        lastName: request?.lastName || prefill.lastName || '',
        addressLine: request?.addressLine || prefill.addressLine || '',
        city: request?.city || prefill.city || '',
        province: request?.province || prefill.province || '',
        postalCode: request?.postalCode || prefill.postalCode || '',
    }));
    const set = (key) => (val) => setForm(f => ({ ...f, [key]: val }));

    const formValid = form.firstName.trim() && form.lastName.trim() && form.addressLine.trim()
        && form.city.trim() && form.province && POSTAL_RE.test(form.postalCode.trim());

    const handleSubmit = async () => {
        if (!formValid || submitting) return;
        setSubmitting(true);
        setError(null);
        try {
            const result = await revisionSheetService.submit({ levelVar: level.var, ...form });
            onSubmitted(level.var, { ...form, postalCode: result.postalCode, status: 'PENDING' });
            setEditing(false);
        } catch (err) {
            setError(err.response?.data?.error || (fr ? 'Erreur lors de l\'envoi' : 'Submission failed'));
        } finally {
            setSubmitting(false);
        }
    };

    const shipped = request?.status === 'SHIPPED';

    return (
        <div className="bg-white dark:bg-[#121212] border border-gray-200 dark:border-white/5 rounded-[1.5rem] p-6 md:p-8 shadow-sm dark:shadow-none space-y-5">
            <div className="flex items-center gap-3">
                <div className="p-2.5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl">
                    <Mail className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                    <h3 className="text-lg font-black text-gray-900 dark:text-white">
                        {fr ? `Fiches de révision ${level.labelFr}` : `${level.labelEn} study sheets`}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        {fr ? 'Envoyées gratuitement par la poste' : 'Shipped to you for free'}
                    </p>
                </div>
                {request && (
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${
                        shipped
                            ? 'bg-green-100 dark:bg-green-500/10 text-green-700 dark:text-green-400'
                            : 'bg-amber-100 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400'
                    }`}>
                        {shipped ? <Truck size={13} /> : <CheckCircle2 size={13} />}
                        {shipped
                            ? (fr ? 'Expédiées' : 'Shipped')
                            : (fr ? 'Demande enregistrée' : 'Request received')}
                    </span>
                )}
            </div>

            {!level.hasAccess && !request ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    {fr
                        ? 'Réservé aux abonnés du cours. Abonne-toi pour recevoir tes fiches !'
                        : 'Available to course subscribers. Subscribe to receive your sheets!'}
                </p>
            ) : !editing && request ? (
                <div className="space-y-3">
                    <div className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <MapPin size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
                        <div>
                            <div className="font-semibold">{request.firstName} {request.lastName}</div>
                            <div>{request.addressLine}</div>
                            <div>{request.city}, {request.province} {request.postalCode}</div>
                            <div className="text-xs text-gray-400 mt-1">
                                {fr ? 'Langue des fiches' : 'Sheet language'} : {request.lang === 'fr' ? 'Français' : 'English'}
                            </div>
                        </div>
                    </div>
                    {shipped ? (
                        <p className="text-xs text-gray-400">
                            {fr
                                ? `Expédiées le ${new Date(request.shippedAt).toLocaleDateString('fr-CA')}. Bonne révision !`
                                : `Shipped on ${new Date(request.shippedAt).toLocaleDateString('en-CA')}. Happy studying!`}
                        </p>
                    ) : (
                        <button onClick={() => setEditing(true)}
                            className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-600 dark:text-blue-400 hover:underline">
                            <Pencil size={12} /> {fr ? 'Corriger mon adresse' : 'Edit my address'}
                        </button>
                    )}
                </div>
            ) : (
                <div className="space-y-4">
                    {/* Langue des fiches */}
                    <div>
                        <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wider">
                            {fr ? 'Langue des fiches' : 'Sheet language'}<span className="text-red-500 ml-0.5">*</span>
                        </label>
                        <div className="flex gap-2">
                            {[['fr', 'Français'], ['en', 'English']].map(([code, label]) => (
                                <button key={code} type="button" onClick={() => set('lang')(code)}
                                    className={`px-4 py-2 rounded-xl text-sm font-bold border-2 transition ${
                                        form.lang === code
                                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300'
                                            : 'border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 hover:border-gray-300'
                                    }`}>
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label={fr ? 'Prénom' : 'First name'} value={form.firstName} onChange={set('firstName')} />
                        <Field label={fr ? 'Nom' : 'Last name'} value={form.lastName} onChange={set('lastName')} />
                    </div>
                    <Field label={fr ? 'Numéro et rue' : 'Street address'} value={form.addressLine} onChange={set('addressLine')}
                        placeholder={fr ? '123 rue Principale, app. 4' : '123 Main Street, apt. 4'} />
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <Field label={fr ? 'Ville' : 'City'} value={form.city} onChange={set('city')} />
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wider">
                                Province<span className="text-red-500 ml-0.5">*</span>
                            </label>
                            <select value={form.province} onChange={e => set('province')(e.target.value)}
                                className="w-full px-3 py-2.5 text-base md:text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
                                <option value="">{fr ? 'Choisir…' : 'Select…'}</option>
                                {CANADIAN_PROVINCES.filter(p => p.code !== 'XX').map(p => (
                                    <option key={p.code} value={p.code}>{fr ? p.fr : p.en}</option>
                                ))}
                            </select>
                        </div>
                        <Field label={fr ? 'Code postal' : 'Postal code'} value={form.postalCode} onChange={set('postalCode')}
                            placeholder="H2X 1Y4" />
                    </div>
                    {form.postalCode.trim() && !POSTAL_RE.test(form.postalCode.trim()) && (
                        <p className="text-xs text-red-500">{fr ? 'Format attendu : A1A 1A1' : 'Expected format: A1A 1A1'}</p>
                    )}
                    {error && (
                        <div className="p-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-xl text-sm text-red-700 dark:text-red-400">
                            {error}
                        </div>
                    )}
                    <div className="flex items-center gap-3">
                        <button onClick={handleSubmit} disabled={!formValid || submitting}
                            className={`flex items-center gap-2 px-6 py-3 rounded-full font-black text-sm transition shadow-lg ${
                                formValid && !submitting
                                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:opacity-90 shadow-blue-500/20'
                                    : 'bg-gray-200 dark:bg-white/5 text-gray-400 dark:text-gray-600 cursor-not-allowed shadow-none'
                            }`}>
                            {submitting && <Loader2 size={15} className="animate-spin" />}
                            {request
                                ? (fr ? 'Mettre à jour mon adresse' : 'Update my address')
                                : (fr ? 'Recevoir mes fiches' : 'Send me my sheets')}
                        </button>
                        {request && (
                            <button onClick={() => setEditing(false)}
                                className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition">
                                {fr ? 'Annuler' : 'Cancel'}
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const RevisionSheets = () => {
    const { language } = useLanguage();
    const fr = language === 'fr';
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        revisionSheetService.getStatus()
            .then(setData)
            .catch(() => setData({ levels: [], prefill: {} }))
            .finally(() => setLoading(false));
    }, []);

    const handleSubmitted = (levelVar, requestData) => {
        setData(prev => ({
            ...prev,
            levels: prev.levels.map(l => (l.var === levelVar ? { ...l, request: { ...(l.request || {}), ...requestData } } : l)),
        }));
    };

    if (loading) {
        return <div className="text-center py-12 text-gray-500">{fr ? 'Chargement...' : 'Loading...'}</div>;
    }

    const levels = data?.levels || [];

    return (
        <div className="pb-12 space-y-6">
            <div>
                <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-1">
                    {fr ? 'Fiches de révision' : 'Study sheets'}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    {fr
                        ? 'Des fiches papier envoyées gratuitement par la poste pour réviser partout, même sans écran.'
                        : 'Printed sheets shipped to you for free — study anywhere, no screen needed.'}
                </p>
            </div>

            {levels.length === 0 ? (
                <div className="bg-white dark:bg-[#121212] border border-gray-200 dark:border-white/5 rounded-[1.5rem] p-8 text-center text-gray-500 dark:text-gray-400">
                    {fr ? 'Aucun niveau ne propose de fiches pour le moment.' : 'No level offers study sheets at the moment.'}
                </div>
            ) : (
                levels.map(level => (
                    <LevelSheetCard key={level.var} level={level} prefill={data.prefill || {}}
                        language={language} onSubmitted={handleSubmitted} />
                ))
            )}
        </div>
    );
};

export default RevisionSheets;
